import React, { useState } from "react";
import { Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";

// Floating orb
const Orb = ({ className, delay = 0 }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl opacity-30 pointer-events-none ${className}`}
    animate={{ y: [0, -24, 0], scale: [1, 1.08, 1] }}
    transition={{ duration: 7 + delay, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

// Stat pill
const Stat = ({ value, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-surface-border rounded-full px-4 py-1.5 shadow-sm"
  >
    <span className="text-sm font-bold text-primary">{value}</span>
    <span className="text-xs text-brand-muted">{label}</span>
  </motion.div>
);

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", url: "" });

  return (
    <section className="relative bg-white overflow-hidden min-h-[calc(100vh-4rem)] flex items-center px-6 py-16">
      {/* Background orbs */}
      <Orb className="w-[500px] h-[500px] bg-primary -top-32 -right-40" delay={0} />
      <Orb className="w-[360px] h-[360px] bg-violet-400 -bottom-24 -left-20" delay={2} />
      <Orb className="w-[260px] h-[260px] bg-blue-300 top-1/3 right-1/4" delay={4} />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{ backgroundImage: "linear-gradient(#4361EE 1px, transparent 1px), linear-gradient(90deg, #4361EE 1px, transparent 1px)", backgroundSize: "40px 40px" }}
      />

      <div className="max-w-2xl mx-auto w-full flex flex-col items-center gap-10 relative z-10">

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2"
        >
          <Stat value="500+" label="Legal teams" delay={0.9} />
          <Stat value="15+"  label="Regulations" delay={1.0} />
          <Stat value="0.1s" label="Analysis speed" delay={1.1} />
        </motion.div>

        {/* Headline */}
        <motion.div className="text-center" variants={container} initial="hidden" animate="show">
          {/* Badge */}
          <motion.div variants={item} className="flex justify-center mb-6">
            <motion.span
              animate={{ boxShadow: ["0 0 0px #4361EE44", "0 0 18px #4361EE66", "0 0 0px #4361EE44"] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="inline-flex items-center gap-2 text-xs font-semibold text-white rounded-full px-4 py-1.5"
              style={{ background: "linear-gradient(90deg,#4361EE,#7C3AED)" }}
            >
              <Zap className="w-3 h-3" />
              Powered by Advanced Legal AI
            </motion.span>
          </motion.div>

          <motion.h1 variants={item} className="text-4xl lg:text-5xl font-extrabold text-brand-text leading-tight mb-2">
            AI Privacy Policy Analyzer
          </motion.h1>
          <motion.h2 variants={item} className="text-3xl lg:text-4xl font-extrabold leading-tight mb-5"
            style={{ background: "linear-gradient(90deg,#4361EE,#7C3AED)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
          >
            for Compliance &amp; Risk
          </motion.h2>
          <motion.p variants={item} className="text-brand-muted text-base leading-relaxed max-w-lg mx-auto">
            Instantly detect legal risks, missing clauses, and compliance gaps in any privacy policy.
            Trusted by legal teams at 500+ companies worldwide.
          </motion.p>
        </motion.div>

        {/* Quick Analysis Card */}
        <motion.div
          className="w-full max-w-xl relative"
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0,  scale: 1 }}
          transition={{ delay: 0.5, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            whileHover={{ y: -4, boxShadow: "0 24px 60px -12px rgba(67,97,238,0.2)" }}
            transition={{ duration: 0.3 }}
            className="bg-white border border-surface-border rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 bg-gray-100 border-b border-surface-border">
              <span className="text-sm font-bold text-brand-text">Quick Policy Analysis</span>
              <span className="text-xs text-brand-light">Paste a policy to analyze</span>
            </div>

            <div className="p-6">
            <div className="space-y-3 mb-5">
              {["Software Name", "Privacy URL"].map((placeholder, i) => (
                <motion.input
                  key={placeholder}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.08, duration: 0.4 }}
                  type="text"
                  placeholder={placeholder}
                  value={form[["name","url"][i]]}
                  onChange={(e) => setForm({ ...form, [["name","url"][i]]: e.target.value })}
                  className="w-full border border-surface-border rounded-lg px-3 py-2.5 text-sm text-brand-text placeholder-brand-light focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                />
              ))}
            </div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button className="w-full justify-center" size="md" onClick={() => navigate("/auth/login")}>
                <Zap className="w-4 h-4" /> Analyze Policy
              </Button>
            </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
